import { isEqual } from 'lodash'
import { ILogger } from './LogTypes'

/**
 * Logs a warning if v1 and v2 are different.
 *
 * Useful for comparing outputs from two different implementations during a parallel refactor.
 */
export const logAndCompare = <T extends object>(
  maybeV1: T | undefined | null,
  maybeV2: T | undefined | null,
  logger: ILogger,
) => {
  if (!maybeV1 && !maybeV2) {
    // Neither object exists, so effectively equal
    return
  }

  if (maybeV1 && !maybeV2) {
    logger.warn('v1 exists but v2 does not')
    return
  }

  if (!maybeV1 && maybeV2) {
    logger.warn('v2 exists but v1 does not')
    return
  }

  const v1 = maybeV1 as T
  const v2 = maybeV2 as T

  const hasValue = (value: unknown) => value !== undefined && value !== null

  // For our purposes, if v1.foo == undefined it is functionally equivalent to v2 not having a key 'foo'
  const v1Keys = Object.keys(v1).filter((key) => hasValue(v1[key as keyof T]))
  const v2Keys = Object.keys(v2).filter((key) => hasValue(v2[key as keyof T]))

  const v1KeySet = new Set(v1Keys)
  const v2KeySet = new Set(v2Keys)

  const commonKeys = v1Keys.filter((key) => v2KeySet.has(key))
  const keysInV1ButNotV2 = v1Keys.filter((key) => !v2KeySet.has(key))
  const keysInV2ButNotV1 = v2Keys.filter((key) => !v1KeySet.has(key))

  const keysWithDifferentValues = []

  for (const key of commonKeys) {
    const v1Value = v1[key as keyof T]
    const v2Value = v2[key as keyof T]

    if (!isEqual(v1Value, v2Value)) {
      // Keys are safe to log, but avoid logging values as they may contain sensitive info
      keysWithDifferentValues.push(key)
    }
  }

  const areDifferent = keysWithDifferentValues.length > 0 || keysInV1ButNotV2.length > 0 || keysInV2ButNotV1.length > 0

  if (areDifferent) {
    logger.warn('v1 and v2 are different', {
      ...(keysWithDifferentValues.length > 0 && { keysWithDifferentValues }),
      ...(keysInV1ButNotV2.length > 0 && { keysInV1ButNotV2 }),
      ...(keysInV2ButNotV1.length > 0 && { keysInV2ButNotV1 }),
    })
  }
}
